import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import { useState, useEffect } from 'react';
import SlideShell from './SlideShell';
import MarkdownRenderer from './MarkdownRenderer';

interface FillInBlankItem {
  id: string;
  prompt: string;
  answer: string;
  accepted_variants?: string[];
  explanation?: string;
}

interface FillInBlankSlideProps {
  slideNumber: number;
  totalSlides: number;
  title: string;
  subtitle: string;
  content: { items: FillInBlankItem[] };
  lessonTitle?: string;
  lessonThumbnail?: string;
  onNext?: () => void;
  onBack?: () => void;
  onBackToLesson?: () => void;
  isCompleted?: boolean;
  onMarkComplete?: () => void;
}

const FillInBlankSlide = ({
  slideNumber,
  totalSlides,
  title,
  subtitle,
  content,
  lessonTitle,
  lessonThumbnail,
  onNext,
  onBack,
  onBackToLesson,
  isCompleted,
  onMarkComplete,
}: FillInBlankSlideProps) => {
  const items = content.items || [];
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [completedItems, setCompletedItems] = useState<Set<number>>(new Set());
  const [answers, setAnswers] = useState<Record<number, {
    userAnswer: string;
    showResult: boolean;
  }>>({});

  const currentItem = items[currentItemIndex];
  const allItemsCompleted = completedItems.size === items.length;

  const checkAnswer = (answer: string): boolean => {
    if (!currentItem) return false;
    
    const normalizedAnswer = answer.trim().toLowerCase();
    const correctAnswer = currentItem.answer.toLowerCase();
    
    if (normalizedAnswer === correctAnswer) return true;
    
    if (currentItem.accepted_variants) {
      return currentItem.accepted_variants.some(
        variant => variant.toLowerCase() === normalizedAnswer
      );
    }
    
    return false;
  };

  const isCorrect = checkAnswer(userAnswer);

  const handleSubmit = () => {
    if (userAnswer.trim()) {
      setShowResult(true);
      setAnswers({
        ...answers,
        [currentItemIndex]: { userAnswer, showResult: true }
      });
      if (isCorrect) {
        setCompletedItems(prev => new Set(prev).add(currentItemIndex));
      }
    }
  };

  const handleNextItem = () => {
    if (currentItemIndex < items.length - 1) {
      setCurrentItemIndex(prev => prev + 1);
    }
  };

  const handlePrevItem = () => {
    if (currentItemIndex > 0) {
      setCurrentItemIndex(prev => prev - 1);
    }
  };

  const handleResetCurrent = () => {
    setUserAnswer('');
    setShowResult(false);
    const newAnswers = { ...answers };
    delete newAnswers[currentItemIndex];
    setAnswers(newAnswers);
    setCompletedItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(currentItemIndex);
      return newSet;
    });
  };

  const handleResetAll = () => {
    setAnswers({});
    setUserAnswer('');
    setShowResult(false);
    setCompletedItems(new Set());
  };

  // Load saved answer when index changes
  useEffect(() => {
    const saved = answers[currentItemIndex];
    setUserAnswer(saved?.userAnswer ?? '');
    setShowResult(saved?.showResult ?? false);
  }, [currentItemIndex, answers]);

  if (!currentItem) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-muted-foreground">No content available</p>
      </div>
    );
  }

  const promptParts = currentItem.prompt.split('__________');

  return (
    <SlideShell
      lessonTitle={lessonTitle}
      lessonThumbnail={lessonThumbnail}
      slideNumber={slideNumber}
      totalSlides={totalSlides}
      subtitle={subtitle}
      onBackToLesson={onBackToLesson}
      onBack={onBack}
      onNext={onNext}
      showMarkComplete={true}
      isCompleted={isCompleted}
      onMarkComplete={allItemsCompleted ? onMarkComplete : undefined}
    >
      <h1 className="font-heading text-2xl mb-6 text-foreground">
        {title}
      </h1>

      {/* Item counter and controls */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          Question {currentItemIndex + 1} of {items.length}
        </p>
        <div className="flex gap-2">
          {showResult && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetCurrent}
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Reset
            </Button>
          )}
          {Object.keys(answers).length > 1 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetAll}
            >
              Reset All
            </Button>
          )}
          {items.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePrevItem}
                disabled={currentItemIndex === 0}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNextItem}
                disabled={currentItemIndex === items.length - 1}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="bg-white rounded-[15px] border border-[#f0f0f0] p-5 mb-6">
        <div className="text-base text-foreground leading-relaxed flex flex-wrap items-center gap-2">
          {promptParts[0] && <MarkdownRenderer content={promptParts[0]} />}
          <span className="inline-block mx-2 px-4 py-1 bg-muted rounded min-w-[200px] text-center">
            {showResult ? (
              <span className={isCorrect ? 'text-[#27c840] font-medium' : 'text-destructive font-medium'}>
                {userAnswer}
              </span>
            ) : (
              <input
                type="text"
                value={userAnswer}
                onChange={(e) => setUserAnswer(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                placeholder="Type your answer..."
                disabled={showResult}
                className="bg-transparent border-none outline-none text-center w-full"
              />
            )}
          </span>
          {promptParts[1] && <MarkdownRenderer content={promptParts[1]} />}
        </div>
      </div>

      {showResult && (
        <>
          <div className={`rounded-[15px] p-4 mb-4 ${isCorrect ? 'bg-[#27c840]/10' : 'bg-destructive/10'}`}>
            <p className="text-sm font-medium mb-1">
              {isCorrect ? '✓ Correct!' : '✗ Incorrect'}
            </p>
            {!isCorrect && (
              <p className="text-sm text-foreground">
                Correct answer: <span className="font-medium">{currentItem.answer}</span>
              </p>
            )}
          </div>

          {currentItem.explanation && (
            <div className="bg-white rounded-[15px] border border-[#f0f0f0] p-4 mb-6">
              <p className="text-sm font-medium mb-2">
                Explanation:
              </p>
              <MarkdownRenderer content={currentItem.explanation} />
            </div>
          )}
        </>
      )}

      {!showResult && (
        <Button
          onClick={handleSubmit}
          disabled={!userAnswer.trim()}
          className="bg-[#27c840] hover:bg-[#22b038] text-white rounded-[12px]"
        >
          Submit Answer
        </Button>
      )}
    </SlideShell>
  );
};

export default FillInBlankSlide;
